import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Mail, CheckCircle2, ArrowLeft } from "lucide-react";

export const ForgotPassword = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      // API is silent-success by design — but network errors surface here
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <SEO title="Forgot password" description="Reset your Axiony password via a secure email link." path="/forgot-password" />
      <div className="hidden lg:block relative grid-bg border-r border-white/10">
        <div className="absolute inset-0 radial-fade" />
        <div className="relative p-16 h-full flex flex-col justify-between">
          <div className="mono-label">password recovery</div>
          <div>
            <h2 className="font-display text-4xl font-semibold tracking-tight max-w-md">
              Lost the key, not the door.
            </h2>
            <p className="mt-4 text-slate-400 max-w-md">
              We'll email you a secure link so you can pick a new password and get back to work.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {!sent ? (
          <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="forgot-password-form">
            <Link to="/login" className="text-sm text-slate-400 hover:text-white flex items-center gap-1" data-testid="back-to-login-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>

            <div>
              <div className="mono-label flex items-center gap-1.5">
                <Mail className="w-3 h-3 text-[#06B6D4]" /> forgot password
              </div>
              <h1 className="font-display text-3xl font-semibold mt-2">Reset your password</h1>
              <p className="text-sm text-slate-400 mt-2">
                Enter your account email. If it exists, we'll send a reset link that expires in 30 minutes.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="bg-[#111827] border-white/10 text-slate-100"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit-btn"
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg py-6"
            >
              {busy ? "Sending link…" : "Send reset link"}
            </Button>
          </form>
        ) : (
          <div className="w-full max-w-md space-y-5" data-testid="forgot-sent-panel">
            <div className="h-12 w-12 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-semibold">Check your inbox</h1>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                If an account exists for <span className="text-slate-200 font-mono">{email}</span>, we've sent a password reset link. It will expire in 30 minutes.
              </p>
              <p className="text-xs text-slate-500 mt-3">
                Didn't receive it? Check spam, or try again in a few minutes — for security, we throttle repeated requests.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/login">
                <Button variant="outline" className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white rounded-lg" data-testid="back-to-login-btn">
                  Back to sign in
                </Button>
              </Link>
              <Button
                variant="ghost"
                onClick={() => setSent(false)}
                data-testid="try-different-email-btn"
                className="text-slate-400 hover:text-white"
              >
                Try a different email
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
